import { PublicClientApplication, type AccountInfo, type RedirectRequest } from "@azure/msal-browser";
import type { PortalConfig } from "./config.ts";
import { registerStepUp } from "./stepUp.ts";

export type OperatorAuth = {
  account: AccountInfo | null;
  signIn(): Promise<void>;
  signOut(): Promise<void>;
  token(mode?: "redirect" | "silent"): Promise<string>;
};

export function operatorWho(account: Pick<AccountInfo, "localAccountId" | "username" | "idTokenClaims"> | null): string | null {
  if (!account) return null;
  const oid = account.idTokenClaims?.oid;
  if (typeof oid === "string" && oid.length > 0) return oid;
  if (account.localAccountId.length > 0) return account.localAccountId;
  if (account.username.length > 0) return account.username;
  return null;
}

export const STEP_UP_WINDOW_SECONDS = 15 * 60;

export const AUTH_TIME_CLAIMS = JSON.stringify({
  id_token: { auth_time: { essential: true } },
  access_token: { auth_time: { essential: true } },
});

export function bearerAuthTime(token: string): number | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), "="));
    const claims = JSON.parse(json) as { auth_time?: unknown };
    return unixSeconds(claims.auth_time);
  } catch {
    return null;
  }
}

export function authTimeStale(authTime: number | null, nowSeconds: number): boolean {
  if (authTime === null) return true;
  return nowSeconds < authTime || nowSeconds - authTime > STEP_UP_WINDOW_SECONDS;
}

function unixSeconds(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === "string" && /^[0-9]+$/.test(value)) return Number(value);
  return null;
}

export function freshSignInRequest(scopes: string[], returnTo: string): RedirectRequest {
  return { scopes, prompt: "login", redirectStartPage: returnTo, claims: AUTH_TIME_CLAIMS };
}

export async function startAuth(config: PortalConfig): Promise<OperatorAuth> {
  const app = new PublicClientApplication({
    auth: {
      clientId: config.clientId,
      authority: `https://login.microsoftonline.com/${config.tenantId}`,
      redirectUri: config.redirectUri,
    },
    cache: { cacheLocation: "sessionStorage" },
  });
  await app.initialize();
  const scopes = [config.apiScope];
  registerStepUp(() => app.loginRedirect(freshSignInRequest(scopes, window.location.href)));
  const redirect = await app.handleRedirectPromise();
  const account = redirect?.account ?? app.getAllAccounts()[0] ?? null;
  let pending: Promise<string> | null = null;

  async function readToken(mode: "redirect" | "silent", current: AccountInfo): Promise<string> {
    try {
      const silent = await app.acquireTokenSilent({ account: current, scopes });
      return silent.accessToken;
    } catch {
      if (mode === "silent") throw new Error("Sign in on the Devices page to open a session.");
      await app.acquireTokenRedirect({ account: current, scopes });
      throw new Error("Redirecting for an access token");
    }
  }

  return {
    account,
    signIn() {
      return app.loginRedirect({ scopes, claims: AUTH_TIME_CLAIMS });
    },
    signOut() {
      return app.logoutRedirect({ account: account ?? undefined });
    },
    token(mode: "redirect" | "silent" = "redirect") {
      if (!account) {
        throw new Error(mode === "silent" ? "Sign in on the Devices page to open a session." : "Sign in required");
      }
      if (pending) return pending;
      const request = readToken(mode, account).finally(() => {
        if (pending === request) pending = null;
      });
      pending = request;
      return request;
    },
  };
}
